/**
 * Tab Coordination System for Authentication
 * 
 * Prevents cross-tab authentication conflicts where multiple tabs
 * compete for auth state management, causing unwanted sign-outs.
 */

interface TabInfo {
  id: string;
  lastActivity: number;
  isMasterTab: boolean;
  currentPath: string;
}

class TabCoordinator {
  private tabId: string;
  private isActive: boolean = true;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private readonly HEARTBEAT_INTERVAL = 5000; // 5 seconds
  private readonly TAB_TIMEOUT = 15000; // 15 seconds

  constructor() {
    this.tabId = this.generateTabId();
    this.setupTabCoordination();
    this.startHeartbeat();
    this.setupVisibilityHandling();
    
    console.log('[TabCoordinator] Initialized tab:', this.tabId);
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private setupTabCoordination() {
    // Register this tab
    this.updateTabInfo();

    // Listen for other tabs
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    window.addEventListener('beforeunload', this.cleanup.bind(this));
  }

  private setupVisibilityHandling() {
    document.addEventListener('visibilitychange', () => {
      this.isActive = !document.hidden;
      if (this.isActive) {
        this.updateTabInfo();
        console.log('[TabCoordinator] Tab became active:', this.tabId);
      }
    });

    window.addEventListener('focus', () => {
      this.isActive = true;
      this.updateTabInfo();
      console.log('[TabCoordinator] Tab focused:', this.tabId);
    });

    window.addEventListener('blur', () => {
      // Don't immediately mark as inactive - let heartbeat handle it
      console.log('[TabCoordinator] Tab blurred:', this.tabId);
    });
  }

  private updateTabInfo() {
    const tabInfo: TabInfo = {
      id: this.tabId,
      lastActivity: Date.now(),
      isMasterTab: this.isMasterTab(),
      currentPath: window.location.pathname
    };

    try {
      localStorage.setItem(`tab_${this.tabId}`, JSON.stringify(tabInfo));
    } catch (e) {
      console.warn('[TabCoordinator] Failed to update tab info:', e);
    }
  }

  private startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.isActive) {
        this.updateTabInfo();
        this.cleanupStaleTabs();
      }
    }, this.HEARTBEAT_INTERVAL);
  }

  private cleanupStaleTabs() {
    const now = Date.now();
    const tabKeys = Object.keys(localStorage).filter(key => key.startsWith('tab_'));

    tabKeys.forEach(key => {
      try {
        const tabInfoStr = localStorage.getItem(key);
        if (tabInfoStr) {
          const tabInfo: TabInfo = JSON.parse(tabInfoStr);
          if (now - tabInfo.lastActivity > this.TAB_TIMEOUT) {
            localStorage.removeItem(key);
            console.log('[TabCoordinator] Cleaned up stale tab:', tabInfo.id);
          }
        }
      } catch (e) {
        // Remove invalid entries
        localStorage.removeItem(key);
      }
    });
  }

  private handleStorageChange(event: StorageEvent) {
    if (event.key?.startsWith('tab_') && event.newValue) {
      try {
        const tabInfo: TabInfo = JSON.parse(event.newValue);
        if (tabInfo.id !== this.tabId) {
          console.log('[TabCoordinator] Other tab activity detected:', tabInfo.id, 'path:', tabInfo.currentPath);
        }
      } catch (e) {
        // Ignore invalid data
      }
    }
  }

  /**
   * Determines if this tab should be the master tab for auth operations
   */
  private isMasterTab(): boolean {
    try {
      const allTabs = this.getAllActiveTabs();
      const activeTabs = allTabs.filter(tab => tab.currentPath !== '/suspended' && tab.currentPath !== '/deactivated');
      
      if (activeTabs.length === 0) {
        // If no active tabs, this tab becomes master
        return true;
      }

      // Master tab is the one with the earliest timestamp among active tabs
      const earliestTab = activeTabs.reduce((earliest, current) => 
        current.lastActivity < earliest.lastActivity ? current : earliest
      );

      return earliestTab.id === this.tabId;
    } catch (e) {
      console.warn('[TabCoordinator] Error determining master tab:', e);
      return true; // Default to master if we can't determine
    }
  }

  private getAllActiveTabs(): TabInfo[] {
    const tabs: TabInfo[] = [];
    const now = Date.now();

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('tab_')) {
        try {
          const tabInfoStr = localStorage.getItem(key);
          if (tabInfoStr) {
            const tabInfo: TabInfo = JSON.parse(tabInfoStr);
            if (now - tabInfo.lastActivity <= this.TAB_TIMEOUT) {
              tabs.push(tabInfo);
            }
          }
        } catch (e) {
          // Ignore invalid entries
        }
      }
    });

    return tabs;
  }

  /**
   * Check if this tab should handle auth operations
   */
  public shouldHandleAuth(): boolean {
    const isMaster = this.isMasterTab();
    const isActiveTab = this.isActive && !document.hidden;
    
    console.log('[TabCoordinator] Auth handling check:', {
      tabId: this.tabId,
      isMaster,
      isActiveTab,
      currentPath: window.location.pathname
    });

    return isMaster && isActiveTab;
  }

  /**
   * Check if this tab should prevent sign-out operations
   */
  public shouldPreventSignOut(reason?: string): boolean {
    const activeTabs = this.getAllActiveTabs();
    const hasOtherActiveTabs = activeTabs.some(tab => 
      tab.id !== this.tabId && 
      tab.currentPath !== '/suspended' && 
      tab.currentPath !== '/deactivated'
    );

    // Prevent sign-out if this tab is not active and there are other active tabs
    const shouldPrevent = !this.isActive && hasOtherActiveTabs;
    
    if (shouldPrevent) {
      console.log('[TabCoordinator] Preventing sign-out from inactive tab:', {
        tabId: this.tabId,
        reason,
        isActive: this.isActive,
        activeTabs: activeTabs.length,
        otherActiveTabs: hasOtherActiveTabs
      });
    }

    return shouldPrevent;
  }

  /**
   * Check if we're in a join flow context that might cause conflicts
   */
  public isInJoinFlowContext(): boolean {
    return window.location.pathname.includes('/join') || 
           window.location.pathname.includes('/profile-setup');
  }

  /**
   * Get information about all active tabs
   */
  public getActiveTabsInfo(): TabInfo[] {
    return this.getAllActiveTabs();
  }

  public getCurrentTabId(): string {
    return this.tabId;
  }

  private cleanup() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
    }

    try {
      localStorage.removeItem(`tab_${this.tabId}`);
    } catch (e) {
      // Ignore cleanup errors
    }

    console.log('[TabCoordinator] Cleaned up tab:', this.tabId);
  }
}

// Export singleton instance
export const tabCoordinator = new TabCoordinator();